import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  asList,
  centsUSD,
  listCountries,
  listProducts,
  type CountryVO,
  type Product,
} from "../lib/api";
import { countryDisplayName } from "../lib/flags";
import { haptic } from "../lib/telegram";
import { useI18n } from "../lib/i18n";
import { Flag } from "../components/Flag";
import { PackageCard } from "../components/PackageCard";
import { IconSearch } from "../components/Icons";

function Chevron() {
  return (
    <svg className="country-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function HomePage() {
  const { t, locale } = useI18n();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [countries, setCountries] = useState<CountryVO[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(q.trim()), 280);
    return () => window.clearTimeout(id);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        // 搜索时同时拉国家 + 套餐；空搜索主展示国家列表（对齐 Flutter Explore）
        const [cData, pData] = await Promise.all([
          listCountries({ q: debounced || undefined, size: 80 }),
          debounced
            ? listProducts({ q: debounced, size: 30 })
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        // 后端 ListCountries 可能忽略 size，客户端截断避免首屏过重
        setCountries(asList<CountryVO>(cData).slice(0, 80));
        setProducts(pData ? asList<Product>(pData) : []);
      } catch (e: unknown) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "加载失败");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const filteredCountries = useMemo(() => {
    if (!debounced) return countries;
    const needle = debounced.toLowerCase();
    return countries.filter((c) => {
      const code = (c.country_code || "").toLowerCase();
      const name = countryDisplayName(c.country_code, locale).toLowerCase();
      return code.includes(needle) || name.includes(needle);
    });
  }, [countries, debounced, locale]);

  return (
    <div>
      <img className="hero-ill" src="/ill/ill_packages.svg" alt="" />
      <h1 className="page-title">{t("heroTitle")}</h1>
      <p className="page-sub">{t("heroSub")}</p>

      <div className="search-wrap">
        <IconSearch className="search-ico" />
        <input
          className="search"
          placeholder={t("searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label={t("searchPlaceholder")}
        />
      </div>

      {err && <div className="error-box">{err}</div>}
      {loading && (
        <div className="empty">
          <div className="spinner" style={{ margin: "0 auto" }} />
        </div>
      )}

      {!loading && filteredCountries.length === 0 && products.length === 0 && (
        <div className="empty">
          <img
            className="empty-ill"
            src="/ill/ill_empty_search.svg"
            alt=""
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <p>{t("noPackages")}</p>
        </div>
      )}

      {!loading && filteredCountries.length > 0 && (
        <>
          <div className="section-label">{t("destinations")}</div>
          <div className="country-list">
            {filteredCountries.map((c) => {
              const code = c.country_code;
              const name = countryDisplayName(code, locale);
              const price =
                c.min_price_cents != null && c.min_price_cents > 0
                  ? t("priceFrom", { price: centsUSD(c.min_price_cents) })
                  : null;
              return (
                <Link
                  key={code}
                  to={`/country/${encodeURIComponent(code)}`}
                  className="country-card"
                  onClick={() => haptic("select")}
                >
                  <Flag code={code} size={44} height={32} shape="rounded" />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="country-name">{name}</div>
                    <div className="country-meta">
                      {code.toUpperCase()} · {t("packagesCount", { n: c.products })}
                    </div>
                  </div>
                  {price && <span className="country-price">{price}</span>}
                  <Chevron />
                </Link>
              );
            })}
          </div>
        </>
      )}

      {!loading && products.length > 0 && (
        <>
          <div className="section-label" style={{ marginTop: 16 }}>
            {t("plansSection")}
          </div>
          <div className="pkg-list">
            {products.map((p) => (
              <PackageCard key={p.id} product={p} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
